import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, NavLink as RouterNavLink } from 'react-router-dom';
import { Image } from 'semantic-ui-react';
import { Menu } from '../../layout/Menu';
import { device } from '../../../theme';
import { ClubHederstegnChart } from './ClubHederstegnChart';

// Import hederstegn statistics data
let hederstegnData = { summary: [], details: {}, clubs: [] };
try {
  hederstegnData = require('../../../data/hederstegn-statistics.json');
} catch (e) {
  console.warn('Hederstegn statistics data not yet generated. Run: npm run generate-hederstegn-statistics');
}

// Import club hederstegn by year data
let clubHederstegnData = {};
try {
  clubHederstegnData = require('../../../data/club-hederstegn-by-year.json');
} catch (e) {
  console.warn('Club hederstegn data not yet generated. Run: npm run generate-club-hederstegn-stats');
}

export const HederstegnStatistics = () => {
  const [viewMode, setViewMode] = useState('personer'); // 'personer' or 'klubber'
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('totalPoints');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [selectedClubForChart, setSelectedClubForChart] = useState(null);

  const currentData = viewMode === 'personer' ? hederstegnData.summary : hederstegnData.clubs;

  const sortedData = useMemo(() => {
    const sorted = [...currentData];
    sorted.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      if (aVal === undefined || aVal === null) aVal = 0;
      if (bVal === undefined || bVal === null) bVal = 0;

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
        if (sortDirection === 'asc') {
          return aVal.localeCompare(bVal);
        } else {
          return bVal.localeCompare(aVal);
        }
      }

      if (sortDirection === 'asc') {
        return aVal - bVal;
      } else {
        return bVal - aVal;
      }
    });
    return sorted;
  }, [currentData, sortColumn, sortDirection]);

  const filteredDataWithRanks = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const dataWithRanks = [];
    let currentRank = 1;
    let previousValue = null;

    sortedData.forEach((person, index) => {
      const currentValue = person[sortColumn];

      if (index > 0 && currentValue !== previousValue) {
        currentRank = index + 1;
      }

      dataWithRanks.push({
        ...person,
        rank: currentRank
      });

      previousValue = currentValue;
    });

    return dataWithRanks.filter(item =>
      !searchTerm ||
      (item.personName && item.personName.toLowerCase().includes(term)) ||
      item.clubName.toLowerCase().includes(term)
    );
  }, [sortedData, searchTerm, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const showPersonDetails = (personName) => {
    setSelectedPerson(hederstegnData.details[personName]);
  };

  return (
    <>
      <Header>
        <div>
          <a href="https://nsff.no">
            <Image src={"/resultater/images/logo-darkgreen.png"} alt="Logo" height={"60"} />
          </a>
        </div>
        <div>
          <h1>Hederstegn statistikk</h1>
        </div>
        <div>
          <Link to={"/"}>Tilbake</Link>
        </div>
      </Header>
      <Menu />
      <Container>
        <ContentWrapper>
          <Title>Hederstegn statistikk</Title>

          <NavLinks>
            <NavLink to="/statistikk" end>Konkurranser</NavLink>
            <NavLink to="/statistikk/personer">Personer</NavLink>
            <NavLink to="/statistikk/klubber">Klubber</NavLink>
            <NavLink to="/statistikk/hederstegn">Hederstegn</NavLink>
            <NavLink to="/statistikk/nsff">NSFF</NavLink>
          </NavLinks>

          <ControlRow>
            <ViewToggle>
              <ToggleButton active={viewMode === 'personer'} onClick={() => setViewMode('personer')}>
                Personer
              </ToggleButton>
              <ToggleButton active={viewMode === 'klubber'} onClick={() => setViewMode('klubber')}>
                Klubber
              </ToggleButton>
            </ViewToggle>
            <SearchGroup>
              <label htmlFor="search">Søk: </label>
              <SearchInput
                id="search"
                type="text"
                placeholder={viewMode === 'personer' ? 'Søk etter navn eller klubb...' : 'Søk etter klubb...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchGroup>
          </ControlRow>

          {filteredDataWithRanks.length > 0 && (
            <>
              <ResultCount>Viser {filteredDataWithRanks.length} {searchTerm && `av ${sortedData.length}`} {viewMode === 'personer' ? 'personer' : 'klubber'}</ResultCount>
              <TableWrapper>
                <StatsTable>
                  <thead>
                    <tr>
                      <th>#</th>
                      {viewMode === 'personer' && (
                        <SortableHeader onClick={() => handleSort('personName')}>
                          Navn {sortColumn === 'personName' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </SortableHeader>
                      )}
                      <SortableHeader onClick={() => handleSort('clubName')}>
                        Klubb {sortColumn === 'clubName' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      {viewMode === 'klubber' && (
                        <SortableHeader onClick={() => handleSort('memberCount')} title="Antall medlemmer med hederstegn">
                          Medlemmer {sortColumn === 'memberCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </SortableHeader>
                      )}
                      <SortableHeader onClick={() => handleSort('totalPoints')} title="Totalt poeng">
                        Tot {sortColumn === 'totalPoints' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('nasjonalPoints')} title="Nasjonal plakett poeng">
                        Nas {sortColumn === 'nasjonalPoints' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('internasjonalPoints')} title="Internasjonal plakett poeng">
                        Int {sortColumn === 'internasjonalPoints' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('ansffPoints')} title="ANSFF poeng">
                        A {sortColumn === 'ansffPoints' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('ensffPoints')} title="ENSFF poeng">
                        E {sortColumn === 'ensffPoints' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('plakettCount')} title="Antall plaketter">
                        #P {sortColumn === 'plakettCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort('hederstegnCount')} title="Antall hederstegn">
                        #H {sortColumn === 'hederstegnCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort(viewMode === 'personer' ? 'hasMNSFF' : 'mnsffCount')} title="Master NSFF">
                        M {sortColumn === (viewMode === 'personer' ? 'hasMNSFF' : 'mnsffCount') && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort(viewMode === 'personer' ? 'hasHonENSFF' : 'honensffCount')} title="Honorary Excellence NSFF">
                        Hon {sortColumn === (viewMode === 'personer' ? 'hasHonENSFF' : 'honensffCount') && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort(viewMode === 'personer' ? 'hasEsNSFF' : 'esnsffCount')} title="Excellence Service NSFF">
                        Es {sortColumn === (viewMode === 'personer' ? 'hasEsNSFF' : 'esnsffCount') && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      <SortableHeader onClick={() => handleSort(viewMode === 'personer' ? 'hasAeresmedlem' : 'aeresmedlemCount')} title="Æresmedlem">
                        Æ {sortColumn === (viewMode === 'personer' ? 'hasAeresmedlem' : 'aeresmedlemCount') && (sortDirection === 'asc' ? '↑' : '↓')}
                      </SortableHeader>
                      {viewMode === 'klubber' && (
                        <>
                          <SortableHeader onClick={() => handleSort('nfffCount')} title="NFFF distinctions">
                            NFFF {sortColumn === 'nfffCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </SortableHeader>
                          <SortableHeader onClick={() => handleSort('fiapCount')} title="FIAP distinctions">
                            FIAP {sortColumn === 'fiapCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </SortableHeader>
                          <SortableHeader onClick={() => handleSort('gpuCount')} title="GPU distinctions">
                            GPU {sortColumn === 'gpuCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </SortableHeader>
                          <SortableHeader onClick={() => handleSort('psaCount')} title="PSA distinctions">
                            PSA {sortColumn === 'psaCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </SortableHeader>
                        </>
                      )}
                      {viewMode === 'personer' && <th>Detaljer</th>}
                      {viewMode === 'klubber' && <th>Tidslinje</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDataWithRanks.map((item, idx) => (
                      <tr key={`${item.clubName}-${idx}`}>
                        <RankCell>{item.rank}</RankCell>
                        {viewMode === 'personer' && <td>{item.personName}</td>}
                        <td>{item.clubName}</td>
                        {viewMode === 'klubber' && <td>{item.memberCount}</td>}
                        <td>{item.totalPoints}</td>
                        <td>{item.nasjonalPoints || '-'}</td>
                        <td>{item.internasjonalPoints || '-'}</td>
                        <td>{item.ansffPoints || '-'}</td>
                        <td>{item.ensffPoints || '-'}</td>
                        <td>{item.plakettCount || '-'}</td>
                        <td>{item.hederstegnCount || '-'}</td>
                        {viewMode === 'personer' ? (
                          <>
                            <td><Checkbox checked={item.hasMNSFF} readOnly /></td>
                            <td><Checkbox checked={item.hasHonENSFF} readOnly /></td>
                            <td><Checkbox checked={item.hasEsNSFF} readOnly /></td>
                            <td><Checkbox checked={item.hasAeresmedlem} readOnly /></td>
                            <td>
                              <DetailsButton onClick={() => showPersonDetails(item.personName)}>
                                Vis
                              </DetailsButton>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{item.mnsffCount || '-'}</td>
                            <td>{item.honensffCount || '-'}</td>
                            <td>{item.esnsffCount || '-'}</td>
                            <td>{item.aeresmedlemCount || '-'}</td>
                            <td>{item.nfffCount || '-'}</td>
                            <td>{item.fiapCount || '-'}</td>
                            <td>{item.gpuCount || '-'}</td>
                            <td>{item.psaCount || '-'}</td>
                            <td>
                              {clubHederstegnData[item.clubName] ? (
                                <DetailsButton onClick={() => setSelectedClubForChart(item.clubName)}>
                                  Vis
                                </DetailsButton>
                              ) : (
                                <span style={{ color: '#999' }}>-</span>
                              )}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </StatsTable>
              </TableWrapper>
            </>
          )}

          {filteredDataWithRanks.length === 0 && (
            <NoData>Ingen treff for søket "{searchTerm}"</NoData>
          )}
        </ContentWrapper>
      </Container>

      {selectedPerson && (
        <Modal onClick={() => setSelectedPerson(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>{selectedPerson.personName}</h2>
              <CloseButton onClick={() => setSelectedPerson(null)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <p><strong>Klubb:</strong> {selectedPerson.clubName}</p>
              <p><strong>Totalt poeng:</strong> {selectedPerson.totalPoints}</p>

              <h3>Hederstegn:</h3>
              {Object.keys(selectedPerson.distinctions).map(code => {
                const dist = selectedPerson.distinctions[code];
                return (
                  <DistinctionSection key={code}>
                    <h4>{dist.typeName}: {dist.points} poeng</h4>
                    {dist.items.length > 0 && (
                      <ul>
                        {dist.items.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    )}
                  </DistinctionSection>
                );
              })}
            </ModalBody>
          </ModalContent>
        </Modal>
      )}

      {selectedClubForChart && clubHederstegnData[selectedClubForChart] && (
        <Modal onClick={() => setSelectedClubForChart(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <ModalHeader>
              <h2>Hederstegn tidslinje: {selectedClubForChart}</h2>
              <CloseButton onClick={() => setSelectedClubForChart(null)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <p style={{ textAlign: 'center', color: '#666', marginBottom: '20px' }}>
                Viser antall hederstegn klubbens medlemmer har mottatt per år
              </p>
              <ClubHederstegnChart data={clubHederstegnData[selectedClubForChart]} />
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

const Container = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 20px;
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  height: 70px;
  background-color: #90b361;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: #36501c;
  font-family: 'Fibon Neue';

  > div {
    display: flex;
    flex-direction: row;
    margin-left: 10px;

    > div {
      margin-left: 20px;
    }

    > a, > h1 {
      color: ${(p) => p.theme.color.primary.base};
    }

    align-items: center;
  }

  > div:nth-child(2) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    font-size: 1.4rem;

    h1 {
      margin: 0;
      font-size: 1.4rem;
      font-weight: normal;
    }
  }

  > div:nth-child(3) {
    margin-right: 20px;
    > a {
      color: ${(p) => p.theme.color.primary.base};
    }
  }
`;

const Title = styled.h1`
  color: #36501c;
  font-family: 'Fibon Neue';
  margin-bottom: 30px;
  text-align: center;
`;

const NavLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;
`;

const NavLink = styled(RouterNavLink)`
  padding: 10px 20px;
  border: 2px solid #90b361;
  border-radius: 4px;
  text-decoration: none;
  color: #36501c;
  font-weight: bold;
  transition: all 0.2s;

  &:hover {
    background-color: #90b361;
    color: white;
  }

  &.active {
    background-color: #36501c;
    border-color: #36501c;
    color: white;
  }
`;

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  justify-content: center;
  margin-bottom: 30px;
  flex-wrap: wrap;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 0;
  border: 2px solid #90b361;
  border-radius: 4px;
  overflow: hidden;
`;

const ToggleButton = styled.button`
  padding: 8px 20px;
  background-color: ${props => props.active ? '#36501c' : 'white'};
  color: ${props => props.active ? 'white' : '#36501c'};
  border: none;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.active ? '#36501c' : '#90b361'};
    color: white;
  }

  &:not(:last-child) {
    border-right: 1px solid #90b361;
  }
`;

const SearchGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }

  label {
    font-weight: bold;
    color: #36501c;

    @media ${device.mobile} {
      align-self: flex-start;
    }
  }
`;

const SearchInput = styled.input`
  padding: 8px 15px;
  border: 2px solid #90b361;
  border-radius: 4px;
  font-size: 16px;
  min-width: 300px;

  &:focus {
    outline: none;
    border-color: #36501c;
  }

  @media ${device.mobile} {
    min-width: 100%;
  }
`;

const ResultCount = styled.div`
  text-align: center;
  margin-bottom: 15px;
  color: #666;
  font-style: italic;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media ${device.mobile} {
    margin-left: -30px;
    margin-right: -30px;
    padding: 0 15px;
  }
`;

const StatsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;

  th, td {
    padding: 8px 6px;
    text-align: left;
    border-bottom: 1px solid #ddd;
    white-space: nowrap;
  }

  th {
    font-size: 0.9em;
  }

  th {
    background-color: #90b361;
    color: #36501c;
    font-weight: bold;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  tbody tr:hover {
    background-color: #f9f9f9;
  }

  td:nth-child(1), td:nth-child(4), td:nth-child(5),
  td:nth-child(6), td:nth-child(7), td:nth-child(8), td:nth-child(9), td:nth-child(10),
  td:nth-child(11), td:nth-child(12), td:nth-child(13), td:nth-child(14),
  th:nth-child(1), th:nth-child(4), th:nth-child(5),
  th:nth-child(6), th:nth-child(7), th:nth-child(8), th:nth-child(9), th:nth-child(10),
  th:nth-child(11), th:nth-child(12), th:nth-child(13), th:nth-child(14) {
    text-align: center;
  }

  @media ${device.mobile} {
    font-size: 10px;
    min-width: 700px;

    th, td {
      padding: 6px 2px;
    }

    th {
      font-size: 0.85em;
    }
  }
`;

const SortableHeader = styled.th`
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: #7a9851;
  }
`;

const RankCell = styled.td`
  font-weight: bold;
  color: #666;
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  cursor: default;
  width: 18px;
  height: 18px;
  accent-color: #90b361;
`;

const DetailsButton = styled.button`
  padding: 5px 10px;
  background-color: #90b361;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;

  &:hover {
    background-color: #7a9851;
  }
`;

const NoData = styled.div`
  text-align: center;
  padding: 50px;
  color: #666;
  font-size: 1.2rem;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 2px solid #90b361;
  padding-bottom: 15px;

  h2 {
    margin: 0;
    color: #36501c;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2em;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #36501c;
  }
`;

const ModalBody = styled.div`
  p {
    margin: 10px 0;
  }

  h3 {
    margin-top: 25px;
    margin-bottom: 15px;
    color: #36501c;
  }

  h4 {
    margin: 15px 0 10px 0;
    color: #555;
    font-size: 1em;
  }

  ul {
    margin: 5px 0;
    padding-left: 25px;
  }

  li {
    margin: 5px 0;
  }
`;

const DistinctionSection = styled.div`
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f9f9f9;
  border-left: 3px solid #90b361;
  border-radius: 4px;
`;
